import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  includeBsadInput,
  mainListingInput,
  mainTemplateColorInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  secondImageOfListingTemplateInput,
  thirdImageOfListingTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  hasRealtorImage,
  getInputImageLabel,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const new_listing_vertical_var_1: templateDataType = {
  templateName: "פלייר נכס חדש - פריסה אנכית",
  displayImageUrl: "/static/creative-template-display-images/real-estate-new-listing-brush-1.jpg",
  description:
    "פלייר מקצועי לרשתות החברתיות עבור נכס חדש בשוק. עיצוב אנכי עם תמונת נכס מרכזית, כיתוב בולט 'נכס חדש', 2 תמונות ופרטי הנכס החשובים",
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    thirdImageOfListingTemplateInput(true), // required for this template
    {
      label: "טקסט נכס חדש",
      type: "text",
      nameInPrompt: "new_title",
      showInSection: "template_specific",
      description: "כותרת 'נכס חדש' שתופיע בגדול",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "נכס חדש";
      }
    },
    {
      label: "כותרת הנכס",
      type: "text",
      nameInPrompt: "property_title",
      showInSection: "template_specific",
      description: "כותרת הנכס שתופיע על רקע ירוק",
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return `נכס יוקרתי ב${mainListing?.address?.city ?? "_____"}`;
      }
    },
    {
      label: "מספר חדרים",
      type: "text",
      nameInPrompt: "room_count",
      showInSection: "template_specific",
      description: "מספר החדרים בנכס",
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return `${mainListing?.roomsCount ?? "____"} חדרים`;
      }
    },
    {
      label: "מתקנים ושירותים",
      type: "text",
      nameInPrompt: "amenities_text",
      showInSection: "template_specific",
      description: "מתקנים ושירותים בנכס או בבניין",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "מעלית • חניה • מרפסת";
      }
    },
    {
      label: "מאפיינים מיוחדים",
      type: "text",
      nameInPrompt: "features_text",
      showInSection: "template_specific",
      description: "מאפיינים מיוחדים של הנכס",
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return `שכונת ${mainListing?.address?.neighborhood ?? "____"}`;
      }
    },
    {
      label: "טקסט בולט אחרון",
      type: "text",
      nameInPrompt: "last_bold_text",
      showInSection: "template_specific",
      description: "טקסט בולט אחרון שיופיע בסוף הרשימה",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "זמין עכשיו";
      }
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const {
      mainColorValue: mainColorValue,
      lighterColorValue,
      darkerColorValue
    } = getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    return `
{
  "description": "Professional Real Estate New Listing Flyer - Vertical Layout",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, modern, eye-catching, premium",
    "aspect_ratio": "9:16 (Vertical/Portrait)"
  },

    ${getVisualStylePrompt(params)},

  "layout": {
    "type": "vertical_sections",
    "sections": [
      {
        "name": "hero_section",
        "height": "50%",
        "content": {
          "background_image": {
            "source": "listing_image",
            "style": "cover, centered"
          },
          "overlay_text": {
            "text": "${textParams.find((param) => param.nameInPrompt === "new_title")?.finalValue}",
            "style": {
              "font_size": "extra_large",
              "font_weight": "bold",
              "color": "white",
              "text_shadow": "Add a drop shadow to clearly separate it from the background",
              "effect": "glossy 3D ${mainColorValue} neon style typography. The face of the letters should be white with a pattern of small ${mainColorValue} dots/halftones.",
              "position": "center overlay"
            }
          }
        }
      },
      {
        "name": "property_title_section", 
        "height": "8%",
        "background": {
          "type": "brush_stroke",
          "color": "${mainColorValue}",
          "style": "bold ${mainColorValue} brush-stroke background underneath the title text only. The brush-stroke background should look hand-painted and organic. Underneath the brush-stroke and below it, the rest of the section background should be white.",
"
        },
        "content": {
          "text": "${
            textParams.find((param) => param.nameInPrompt === "property_title")?.finalValue
          }",
          "font_color": "white",
          "font_size": "large",
          "font_weight": "bold",
          "text_align": "center"
        }
      },
      {
        "name": "info_bar_section",
        "height": "10%", 
        "background_color": "white",
        "border_radius": "rounded_rectangular",
        "content": {
          "type": "four_column_grid",
          "columns": [
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "room_count")?.finalValue
              }",
              "style": "minimalist text, dark color"
            },
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "amenities_text")?.finalValue
              }",
              "style": "minimalist text, dark color"
            },
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "features_text")?.finalValue
              }",
              "style": "minimalist text, dark color"
            },
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "last_bold_text")?.finalValue
              }",
              "style": "minimalist text, ${mainColorValue} color emphasis"
            }
          ]
        }
      },
      {
        "name": "additional_images_section",
        "height": "20%",
        "background_color": "white",
        "content": {
          "type": "side_by_side_images",
          "alignment": "centered",
          images_rotation: "none - keep images straight with original orientation",
          "images": [
            {
              "source": "listing_image_2", 
              "style": "rounded_square, left positioned"
            },
            {
              "source": "listing_image_3",
              "style": "rounded_square, right positioned"
            }
          ],
          "spacing": "small gap between images"
        }
      },
      ${
        hasRealtorImage(params)
          ? `{
       ${getRealtorImagePrompt(params, `The realtor should overlap the footer to create depth.`)}
       },`
          : ""
      }
      {
        ${getVerticalFooterPrompt(params)}
      }
    ]
  },

  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    "listing_image_2": "${getInputImageLabel("listing_image_2")}",
    "listing_image_3": "${getInputImageLabel("listing_image_3")}",
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },${getBsadPrompt(params)}

  "important_notes": [
    "Ensure vertical/portrait aspect ratio (9:16) suitable for Instagram Stories and social media",
    "The '${
      textParams.find((param) => param.nameInPrompt === "new_title")?.finalValue
    }' text should be prominent and eye-catching with 3D ${mainColorValue} neon effect",
    "Maintain professional real estate marketing standards",
    "All Hebrew text should be right-to-left and properly formatted",
    "DO NOT alter the provided images beyond removing backgrounds and resizing/cropping as necessary",
    "Info bar should be clean and minimalist with clear separation between sections"
  ]
}`;
  }
};
